import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { fetchMiniSite, SableApiError } from "../api/sableApi";
// The "mini_site" presentation type (Mini Site proposal, customer-door-hanger-qr-dashboard.
// 2026-10-13) -- Template 1: a customer's own system record (header/service-history/payment-info,
// §4.1), fetched from sable-api's read-proxy (`/cards/{token}/mini-site`), which itself proxies to
// docs-api server-to-server. No live chat, no editing here -- same "developer-reviewed content,
// not live generated" spirit as StorytellerExperience, just read-only instead of pre-narrated.
//
// Visual redesign 2026-10-14: clean/modern utility-portal look (teal/slate palette, card system,
// timeline-style history, dark mode, print styles). All data bindings unchanged -- this only
// touches markup/CSS. Scoped under `.mini-site-root` so its rules don't leak into the rest of the
// `www` SPA shell.
function formatVisitDate(raw) {
    // Parsed/formatted as UTC throughout -- `raw` is a plain YYYY-MM-DD calendar date with no time
    // component, so letting the browser's local timezone shift it (the default for `new Date(raw)` +
    // `toLocaleDateString`) can silently roll it back a day for any timezone behind UTC.
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (!match) {
        return { display: raw, iso: raw };
    }
    const [, year, month, day] = match;
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return {
        display: parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }),
        iso: raw,
    };
}
function toTelHref(phone) {
    const digits = phone.replace(/[^\d]/g, "");
    if (digits.length === 10)
        return `tel:+1${digits}`;
    if (digits.length === 11)
        return `tel:+${digits}`;
    return `tel:${digits}`;
}
export default function MiniSiteExperience({ token, directory }) {
    const [site, setSite] = useState(null);
    const [error, setError] = useState(null);
    const accent = directory.accent ?? "#1F4E5F";
    useEffect(() => {
        let cancelled = false;
        fetchMiniSite(token)
            .then((result) => {
            if (!cancelled)
                setSite(result);
        })
            .catch((err) => {
            if (cancelled)
                return;
            setError(err instanceof SableApiError ? err.message : "Couldn't load this card.");
        });
        return () => {
            cancelled = true;
        };
    }, [token]);
    if (error) {
        return (_jsx(Shell, { accent: accent, children: _jsx("div", { className: "ms-card", role: "alert", children: _jsx("p", { className: "ms-error-text", children: error }) }) }));
    }
    if (!site) {
        return (_jsx(Shell, { accent: accent, children: _jsx("div", { className: "ms-card", children: _jsx("p", { className: "ms-muted", children: "Loading\u2026" }) }) }));
    }
    const latest = site.visits[0];
    const { display: lastServicedDisplay, iso: lastServicedIso } = latest
        ? formatVisitDate(latest.visitDate)
        : { display: null, iso: null };
    const needsAttention = Boolean(latest?.needsAttention);
    const callHref = directory.phone ? toTelHref(directory.phone) : null;
    return (_jsxs(Shell, { accent: accent, children: [_jsx("header", { className: "ms-header", children: _jsxs("div", { className: "ms-header-brand", children: [_jsx("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", width: "18", height: "18", fill: "none", children: _jsx("path", { d: "M12 2c3.5 4.5 7 8.6 7 12.5A7 7 0 0 1 5 14.5C5 10.6 8.5 6.5 12 2Z", fill: "currentColor", opacity: "0.9" }) }), _jsx("span", { children: directory.business ?? "Service record" })] }) }), _jsxs("main", { children: [_jsxs("section", { className: "ms-customer-card", "aria-label": "Customer and facility details", children: [_jsx("h1", { className: "ms-customer-name", children: site.permitteeName }), _jsxs("div", { className: "ms-address", children: [_jsxs("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", width: "15", height: "15", fill: "none", children: [_jsx("path", { d: "M12 21s7-6.1 7-11.5A7 7 0 0 0 5 9.5C5 14.9 12 21 12 21Z", stroke: "currentColor", strokeWidth: "1.6", strokeLinejoin: "round" }), _jsx("circle", { cx: "12", cy: "9.5", r: "2.4", stroke: "currentColor", strokeWidth: "1.6" })] }), _jsx("span", { children: site.facilityLocation })] }), _jsxs("span", { className: "ms-chip", children: ["Reg #", site.registrationNumber] }), latest && (_jsxs("div", { className: "ms-status-strip", children: [_jsxs("span", { className: `ms-pill ${needsAttention ? "ms-pill-warning" : "ms-pill-success"}`, children: [_jsx("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", width: "13", height: "13", fill: "none", children: needsAttention ? (_jsx("path", { d: "M12 4v9m0 4h.01M4 19h16", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })) : (_jsx("path", { d: "M5 12.5 9.5 17 19 7", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })) }), needsAttention ? "Needs attention" : "Operating normally"] }), _jsxs("span", { className: "ms-muted ms-last-serviced", children: ["Last serviced", " ", _jsx("time", { dateTime: lastServicedIso ?? undefined, children: lastServicedDisplay })] })] }))] }), _jsxs("section", { className: "ms-section", "aria-label": "Service history", children: [_jsx("h2", { className: "ms-section-heading", children: "Service history" }), site.visits.length === 0 ? (_jsx("p", { className: "ms-muted", children: "No visits on file yet." })) : (_jsx("ul", { className: "ms-timeline", children: site.visits.map((visit, index) => {
                                    const { display, iso } = formatVisitDate(visit.visitDate);
                                    return (_jsxs("li", { className: "ms-timeline-item", children: [_jsx("span", { className: `ms-timeline-dot ${visit.needsAttention ? "ms-dot-warning" : "ms-dot-success"}`, "aria-hidden": "true" }), _jsxs("div", { className: "ms-card ms-timeline-card", children: [_jsxs("div", { className: "ms-timeline-top", children: [_jsx("time", { className: "ms-visit-date", dateTime: iso, children: display }), visit.needsAttention && _jsx("span", { className: "ms-tag-warning", children: "Needs attention" })] }), _jsx("p", { className: "ms-visit-summary", children: visit.summary }), visit.technician && (_jsxs("div", { className: "ms-technician", children: [_jsxs("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", width: "13", height: "13", fill: "none", children: [_jsx("circle", { cx: "12", cy: "8", r: "3.2", stroke: "currentColor", strokeWidth: "1.6" }), _jsx("path", { d: "M5 20c1.2-3.8 4-5.8 7-5.8s5.8 2 7 5.8", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round" })] }), "Serviced by ", visit.technician] }))] })] }, `${visit.visitDate}-${index}`));
                                }) }))] }), _jsxs("section", { className: "ms-card ms-section", "aria-label": "Payment information", children: [_jsx("h2", { className: "ms-section-heading", children: "Payment" }), _jsx("p", { className: "ms-payment-text", children: site.paymentInfoText }), callHref && directory.phone && (_jsxs("a", { className: "ms-call-button", href: callHref, children: [_jsx("svg", { "aria-hidden": "true", viewBox: "0 0 24 24", width: "17", height: "17", fill: "none", children: _jsx("path", { d: "M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1.1.5 1.1 1.1V20c0 .6-.5 1.1-1.1 1.1C10.6 21.1 2.9 13.4 2.9 3.8 2.9 3.2 3.4 2.7 4 2.7h3.4c.6 0 1.1.5 1.1 1.1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1L6.6 10.8Z", fill: "currentColor" }) }), "Call ", directory.phone] }))] })] }), _jsxs("footer", { className: "ms-footer", children: ["\u00A9 ", directory.business ?? "McClure Services"] })] }));
}
function Shell({ accent, children }) {
    return (_jsxs("div", { className: "mini-site-root", style: { ["--ms-primary"]: accent }, children: [_jsx("style", { children: CSS }), children] }));
}
const CSS = `
.mini-site-root {
  --ms-primary: #1F4E5F;
  --ms-primary-tint: #E6F0F3;
  --ms-bg: #F3F5F7;
  --ms-card-bg: #FFFFFF;
  --ms-border: #E3E8EC;
  --ms-text: #1B2630;
  --ms-muted: #6B7885;
  --ms-success: #1E8E5A;
  --ms-success-tint: #E5F5EC;
  --ms-warning: #8A6D00;
  --ms-warning-tint: #FFF4D9;

  min-height: 100vh;
  background: var(--ms-bg);
  color: var(--ms-text);
  font-family: -apple-system, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  max-width: 480px;
  margin: 0 auto;
}

.mini-site-root :where(*) { box-sizing: border-box; }

.mini-site-root a { color: inherit; }

.mini-site-root .ms-header {
  background: var(--ms-primary);
  color: #fff;
  padding: 20px 20px 48px;
  border-radius: 0 0 24px 24px;
}

.mini-site-root .ms-header-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.mini-site-root main {
  padding: 0 16px 32px;
}

.mini-site-root .ms-customer-card {
  background: var(--ms-card-bg);
  border: 1px solid var(--ms-border);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04);
  padding: 20px;
  margin: -32px 4px 20px;
  position: relative;
}

.mini-site-root .ms-customer-name {
  font-size: clamp(1.25rem, 1.1rem + 1vw, 1.5rem);
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.25;
}

.mini-site-root .ms-address {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: var(--ms-muted);
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.mini-site-root .ms-address svg { flex-shrink: 0; margin-top: 2px; }

.mini-site-root .ms-chip {
  display: inline-block;
  background: var(--ms-primary-tint);
  color: var(--ms-primary);
  font-size: 0.78rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
}

.mini-site-root .ms-status-strip {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--ms-border);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.mini-site-root .ms-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 999px;
}

.mini-site-root .ms-pill-success { background: var(--ms-success-tint); color: var(--ms-success); }
.mini-site-root .ms-pill-warning { background: var(--ms-warning-tint); color: var(--ms-warning); }

.mini-site-root .ms-last-serviced { font-size: 0.82rem; }

.mini-site-root .ms-section { margin-top: 24px; }

.mini-site-root .ms-section-heading {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 14px;
  padding: 0 4px;
}

.mini-site-root .ms-timeline {
  list-style: none;
  margin: 0;
  padding: 0 4px;
}

.mini-site-root .ms-timeline-item {
  display: grid;
  grid-template-columns: 16px 1fr;
  column-gap: 12px;
  position: relative;
}

.mini-site-root .ms-timeline-item:not(:last-child) {
  padding-bottom: 14px;
}

.mini-site-root .ms-timeline-item:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 7px;
  top: 16px;
  bottom: 0;
  width: 2px;
  background: var(--ms-border);
}

.mini-site-root .ms-timeline-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin-top: 4px;
  border: 3px solid var(--ms-card-bg);
  box-shadow: 0 0 0 1px var(--ms-border);
}

.mini-site-root .ms-dot-success { background: var(--ms-success); }
.mini-site-root .ms-dot-warning { background: var(--ms-warning); }

.mini-site-root .ms-card {
  background: var(--ms-card-bg);
  border: 1px solid var(--ms-border);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04);
  padding: 16px 18px;
}

.mini-site-root .ms-timeline-card { margin-bottom: 2px; }

.mini-site-root .ms-timeline-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.mini-site-root .ms-visit-date { font-weight: 700; font-size: 0.95rem; }

.mini-site-root .ms-tag-warning {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--ms-warning);
  background: var(--ms-warning-tint);
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.mini-site-root .ms-visit-summary {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
}

.mini-site-root .ms-technician {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 0.78rem;
  color: var(--ms-muted);
}

.mini-site-root .ms-payment-text {
  margin: 0 0 16px;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--ms-text);
}

.mini-site-root .ms-call-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  background: var(--ms-primary);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 12px;
  text-decoration: none;
  transition: opacity 0.15s ease;
}

.mini-site-root .ms-call-button:hover { opacity: 0.92; }

.mini-site-root a:focus-visible,
.mini-site-root button:focus-visible {
  outline: 2px solid var(--ms-primary);
  outline-offset: 2px;
}

.mini-site-root .ms-muted { color: var(--ms-muted); }
.mini-site-root .ms-error-text { color: #b3261e; margin: 0; }

.mini-site-root .ms-footer {
  text-align: center;
  color: var(--ms-muted);
  font-size: 0.78rem;
  padding: 24px 16px 40px;
}

@media (prefers-color-scheme: dark) {
  .mini-site-root {
    --ms-bg: #0F1519;
    --ms-card-bg: #182229;
    --ms-border: #263038;
    --ms-text: #E8EEF2;
    --ms-muted: #8A97A3;
    --ms-primary-tint: #1C333B;
    --ms-success-tint: #123523;
    --ms-warning-tint: #332A08;
  }
}

@media print {
  .mini-site-root { background: #fff; }
  .mini-site-root .ms-header { background: none; color: #000; border-radius: 0; }
  .mini-site-root .ms-card,
  .mini-site-root .ms-customer-card { box-shadow: none; }
  .mini-site-root .ms-call-button { display: none; }
}
`;
